import React, { useState } from 'react'

export type ButtonProps = {
  color?: string
  invert?: boolean
  label?: string
  submit?: boolean
  size?: 'medium' | 'small' | 'lg' | 'xl'
  onClick?: () => void
  disabled?: boolean
  isLoading?: boolean
  apply?: boolean
}

export const buttonClassName = ({
  invert,
  size,
  apply,
}: {
  invert: ButtonProps['invert']
  size?: ButtonProps['size']
  apply?: ButtonProps['apply']
}): string => {
  let className =
    'px-6 py-1 font-semibold tracking-wide transition-colors duration-300 ease-in-out border-2 rounded-full'

  switch (size) {
    case 'small':
      className =
        'px-3 py-1 text-xs font-medium transition-colors duration-300 ease-in-out border rounded-full'
      break
    case 'medium':
      className =
        'px-4 py-2 text-sm font-medium transition-colors duration-300 ease-in-out border rounded-full'
      break
    case 'lg':
      className =
        'px-6 py-2 font-medium transition-colors duration-300 ease-in-out border rounded-full lg:text-lg text-md'
      break
    case 'xl':
      className =
        'px-8 py-2 font-medium transition-colors duration-300 ease-in-out border rounded-full lg:text-xl text-md'
      break
    default:
  }

  if (apply) {
    if (invert) {
      className +=
        ' border-2 border-primary hover:border-primary hover:border-2 disabled:border-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:bg-gray-300'
    } else {
      className +=
        ' border-primary hover:border-white disabled:border-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:bg-gray-300'
    }
  }

  return className
}

const ButtonColor: React.FC<ButtonProps> = ({
  color = 'primary',
  invert = false,
  label = '',
  submit = false,
  size = undefined,
  onClick = undefined,
  isLoading = false,
  disabled = false,
  apply = false,
}) => {
  const [isHover, setIsHover] = useState(false)

  const handleMouseEnter = () => {
    setIsHover(true)
  }
  const handleMouseLeave = () => {
    setIsHover(false)
  }

  const buttonClasses = `${buttonClassName({ invert, size, apply })}`

  let backgroundColor = color
  const borderColor = color
  let textColor = 'white'

  if (color && color.length === 7 && color[0] === '#') {
    if (disabled) {
      backgroundColor = 'lightgray'
      textColor = 'gray'
    } else if (isHover) {
      backgroundColor = 'white'
      textColor = color
    }

    if (!invert) {
      backgroundColor = 'white'
      textColor = color
      if (isHover) {
        backgroundColor = color
        textColor = 'white'
      }
    }

    const style = {
      backgroundColor,
      borderColor,
      color: textColor,
    }

    return (
      <button
        className={buttonClasses}
        type={submit ? 'submit' : 'button'}
        onClick={onClick}
        disabled={disabled}
        style={style}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {isLoading ? (
          <span className="flex items-center justify-center w-full h-full">
            <svg
              className="w-6 h-6 text-white-400 animate-spin"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </span>
        ) : (
          label
        )}
      </button>
    )
  }
  return (
    <button
      className={buttonClasses}
      type={submit ? 'submit' : 'button'}
      onClick={onClick}
      disabled={disabled}
    >
      {isLoading ? (
        <span className="flex items-center justify-center w-full h-full">
          <svg
            className="w-6 h-6 text-white-400 animate-spin"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        </span>
      ) : (
        label
      )}
    </button>
  )
}

export default ButtonColor
